/**
 * financePermissions.ts — Issue #134
 *
 * Finance module permission constants and helpers.
 * Mirrors backend @RequirePermission('finance.*', '...') decorators.
 *
 * Design principles:
 * - No React imports; pure TypeScript for testability.
 * - Permission strings follow `<resource>:<action>` pattern.
 * - Wildcards: `*:*`, `<resource>:*`, `*:<action>`.
 */

import { hasPermission, hasAnyPermission } from './crmPermissions';

// Re-export helpers for convenience
export { hasPermission, hasAnyPermission };

// ─── Finance Permission Constants ─────────────────────────────────────────────

export const FINANCE_PERMISSIONS = {
  // OrderProject
  ORDER_PROJECT_CREATE: 'finance.order_project:create',
  ORDER_PROJECT_READ: 'finance.order_project:read',

  // PaymentRecord
  PAYMENT_RECORD_CREATE: 'finance.payment_record:create',
  PAYMENT_RECORD_READ: 'finance.payment_record:read',
  PAYMENT_RECORD_CONFIRM: 'finance.payment_record:confirm',
  PAYMENT_RECORD_VOID: 'finance.payment_record:void',
  PAYMENT_RECORD_REVERSE: 'finance.payment_record:reverse',

  // ProjectUnlock
  PROJECT_UNLOCK_READ: 'finance.project_unlock:read',
  PROJECT_UNLOCK_SPECIAL_OVERRIDE: 'finance.project_unlock:special_override',

  // RevenueLedger
  REVENUE_LEDGER_CREATE: 'finance.revenue_ledger:create',
  REVENUE_LEDGER_READ: 'finance.revenue_ledger:read',
  REVENUE_LEDGER_UNLOCK: 'finance.revenue_ledger:unlock',
  REVENUE_LEDGER_PAY_OFF: 'finance.revenue_ledger:pay_off',

  // CommissionLedger
  COMMISSION_LEDGER_CREATE: 'finance.commission_ledger:create',
  COMMISSION_LEDGER_READ: 'finance.commission_ledger:read',
  COMMISSION_LEDGER_PAY_OFF: 'finance.commission_ledger:pay_off',

  // Issue #329 — Invoice
  INVOICE_READ: 'finance.invoice:read',
  INVOICE_CREATE: 'finance.invoice:create',
  INVOICE_ISSUE: 'finance.invoice:issue',
  INVOICE_VOID: 'finance.invoice:void',

  // Issue #329 — Installment
  INSTALLMENT_READ: 'finance.installment:read',
  INSTALLMENT_CREATE: 'finance.installment:create',
  INSTALLMENT_UPDATE: 'finance.installment:update',
  INSTALLMENT_FOLLOW_UP: 'finance.installment:follow_up',

  // Issue #329 — Adjustment
  ADJUSTMENT_READ: 'finance.adjustment:read',
  ADJUSTMENT_CREATE: 'finance.adjustment:create',
  ADJUSTMENT_REVIEW: 'finance.adjustment:review',
  ADJUSTMENT_EXECUTE: 'finance.adjustment:execute',

  // Issue #329 — Summary
  SUMMARY_READ: 'finance.summary:read',

  // Issue #368 — Finance Template base
  TEMPLATE_READ: 'finance.template:read',
  TEMPLATE_RENDER: 'finance.template:render',
  TEMPLATE_MANAGE: 'finance.template:manage',

  // Issue #369 — Finance Counterparty + Payee Account master
  COUNTERPARTY_READ: 'finance.counterparty:read',
  COUNTERPARTY_WRITE: 'finance.counterparty:write',
  COUNTERPARTY_REVEAL: 'finance.counterparty:reveal',
  COUNTERPARTY_MANAGE: 'finance.counterparty:manage',
  PAYEE_ACCOUNT_READ: 'finance.payee_account:read',
  PAYEE_ACCOUNT_WRITE: 'finance.payee_account:write',
  PAYEE_ACCOUNT_REVEAL: 'finance.payee_account:reveal',
  PAYEE_ACCOUNT_VERIFY: 'finance.payee_account:verify',
  PAYEE_ACCOUNT_MANAGE: 'finance.payee_account:manage',
  PAYEE_ACCOUNT_SNAPSHOT: 'finance.payee_account:snapshot',

  // Issue #370 — Daily settlement workflows.
  // Codex audit #370 requires department approval and finance review to be
  // protected by distinct RBAC actions so manager/boss cannot reach finance
  // review and finance cannot reach department approval.
  PAYMENT_APPLICATION_READ: 'finance.payment_application:read',
  PAYMENT_APPLICATION_WRITE: 'finance.payment_application:write',
  PAYMENT_APPLICATION_DEPARTMENT_APPROVE:
    'finance.payment_application:department_approve',
  PAYMENT_APPLICATION_FINANCE_REVIEW:
    'finance.payment_application:finance_review',
  PAYMENT_APPLICATION_EXTRA_APPROVE: 'finance.payment_application:extra_approve',
  PAYMENT_APPLICATION_CASHIER_EXECUTE:
    'finance.payment_application:cashier_execute',
  PAYMENT_APPLICATION_MANAGE: 'finance.payment_application:manage',
  EMPLOYEE_ADVANCE_READ: 'finance.employee_advance:read',
  EMPLOYEE_ADVANCE_WRITE: 'finance.employee_advance:write',
  EMPLOYEE_ADVANCE_DEPARTMENT_APPROVE:
    'finance.employee_advance:department_approve',
  EMPLOYEE_ADVANCE_FINANCE_REVIEW: 'finance.employee_advance:finance_review',
  EMPLOYEE_ADVANCE_CASHIER_EXECUTE: 'finance.employee_advance:cashier_execute',
  EMPLOYEE_ADVANCE_MANAGE: 'finance.employee_advance:manage',
  EXPENSE_REIMBURSEMENT_READ: 'finance.expense_reimbursement:read',
  EXPENSE_REIMBURSEMENT_WRITE: 'finance.expense_reimbursement:write',
  EXPENSE_REIMBURSEMENT_DEPARTMENT_APPROVE:
    'finance.expense_reimbursement:department_approve',
  EXPENSE_REIMBURSEMENT_FINANCE_REVIEW:
    'finance.expense_reimbursement:finance_review',
  EXPENSE_REIMBURSEMENT_CASHIER_EXECUTE:
    'finance.expense_reimbursement:cashier_execute',
  EXPENSE_REIMBURSEMENT_MANAGE: 'finance.expense_reimbursement:manage',

  // Issue #371 — Payroll workbook import + payout governance.
  PAYROLL_READ: 'finance.payroll:read',
  PAYROLL_IMPORT: 'finance.payroll:import',
  PAYROLL_HR_REVIEW: 'finance.payroll:hr_review',
  PAYROLL_FINANCE_REVIEW: 'finance.payroll:finance_review',
  PAYROLL_CASHIER_PAYOUT: 'finance.payroll:cashier_payout',
  PAYROLL_REVEAL: 'finance.payroll:reveal',
  PAYROLL_MANAGE: 'finance.payroll:manage',

  // Issue #372 — Multi-stage commission rules / details / events / payout
  // candidates / template-style export. `write` (rule + detail) is the
  // mutation gate kept separate from `:read`, so manager / boss with read
  // only cannot create or modify commission details.
  COMMISSION_RULE_READ: 'finance.commission_rule:read',
  COMMISSION_RULE_WRITE: 'finance.commission_rule:write',
  COMMISSION_RULE_ACTIVATE: 'finance.commission_rule:activate',
  COMMISSION_DETAIL_READ: 'finance.commission_detail:read',
  COMMISSION_DETAIL_WRITE: 'finance.commission_detail:write',
  COMMISSION_DETAIL_FREEZE: 'finance.commission_detail:freeze',
  COMMISSION_DETAIL_CLAWBACK: 'finance.commission_detail:clawback',
  // Reveal full commission amounts (separate from rendering masked sheet).
  COMMISSION_DETAIL_REVEAL: 'finance.commission_detail:reveal',
  COMMISSION_EVENT_READ: 'finance.commission_event:read',
  COMMISSION_PAYOUT_CANDIDATE_READ:
    'finance.commission_payout_candidate:read',
  COMMISSION_PAYOUT_CANDIDATE_BUILD:
    'finance.commission_payout_candidate:build',
  COMMISSION_PAYOUT_CANDIDATE_CONFIRM:
    'finance.commission_payout_candidate:confirm',
  COMMISSION_EXPORT_RENDER: 'finance.commission_export:render',

  // Issue #402 — Unified Finance Audit View (cross-source: 资金 / 工资 /
  // 提成 / 渠道分成 / 行政采购 / 冲正等 14 类来源)。Mirrors backend
  // @RequirePermission('finance.audit_view', '...') decorators.
  AUDIT_VIEW_READ_FULL: 'finance.audit_view:read_full',
  AUDIT_VIEW_READ_TEAM: 'finance.audit_view:read_team',
  AUDIT_VIEW_READ_SELF: 'finance.audit_view:read_self',
  AUDIT_VIEW_READ_CHANNEL: 'finance.audit_view:read_channel',
  AUDIT_VIEW_REVEAL: 'finance.audit_view:reveal',
  AUDIT_VIEW_EXPORT: 'finance.audit_view:export',

  // Issue #373 — Sales management standard calculation report.
  SALES_REPORT_READ: 'finance.sales_report:read',
  SALES_REPORT_GENERATE: 'finance.sales_report:generate',
  SALES_REPORT_REVIEW: 'finance.sales_report:review',
  SALES_REPORT_PUBLISH: 'finance.sales_report:publish',
  SALES_REPORT_EXPORT: 'finance.sales_report:export',
  SALES_REPORT_REVEAL: 'finance.sales_report:reveal',
  SALES_REPORT_MANAGE: 'finance.sales_report:manage',
} as const;

export type FinancePermission = (typeof FINANCE_PERMISSIONS)[keyof typeof FINANCE_PERMISSIONS];

// ─── Finance Menu Visibility Helpers ─────────────────────────────────────────

/**
 * Returns true if the Finance top-level sidebar entry should be visible.
 * The user needs at least one Finance read permission.
 */
export function canSeeFinanceMenu(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    FINANCE_PERMISSIONS.PAYMENT_RECORD_READ,
    FINANCE_PERMISSIONS.PROJECT_UNLOCK_READ,
    FINANCE_PERMISSIONS.REVENUE_LEDGER_READ,
    FINANCE_PERMISSIONS.COMMISSION_LEDGER_READ,
    FINANCE_PERMISSIONS.INVOICE_READ,
    FINANCE_PERMISSIONS.INSTALLMENT_READ,
    FINANCE_PERMISSIONS.ADJUSTMENT_READ,
    FINANCE_PERMISSIONS.SUMMARY_READ,
    FINANCE_PERMISSIONS.TEMPLATE_READ,
    FINANCE_PERMISSIONS.COUNTERPARTY_READ,
    FINANCE_PERMISSIONS.PAYEE_ACCOUNT_READ,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_READ,
    FINANCE_PERMISSIONS.EMPLOYEE_ADVANCE_READ,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_READ,
    FINANCE_PERMISSIONS.PAYROLL_READ,
    FINANCE_PERMISSIONS.COMMISSION_RULE_READ,
    FINANCE_PERMISSIONS.COMMISSION_DETAIL_READ,
    FINANCE_PERMISSIONS.COMMISSION_PAYOUT_CANDIDATE_READ,
    FINANCE_PERMISSIONS.COMMISSION_EXPORT_RENDER,
    FINANCE_PERMISSIONS.SALES_REPORT_READ,
    // Issue #402 — any audit-view scope is enough to surface the entry; the
    // page itself further gates reveal / export actions.
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_FULL,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_TEAM,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_SELF,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_CHANNEL,
  ]);
}

export function canSeePayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_READ);
}

export function canImportPayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_IMPORT);
}

export function canHrReviewPayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_HR_REVIEW);
}

export function canFinanceReviewPayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_FINANCE_REVIEW);
}

export function canCashierPayoutPayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_CASHIER_PAYOUT);
}

export function canRevealPayrollField(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_REVEAL);
}

export function canManagePayroll(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_MANAGE);
}

export function canSeeFinanceCounterparties(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.COUNTERPARTY_READ);
}

export function canRevealCounterpartyField(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.COUNTERPARTY_REVEAL);
}

export function canRevealPayeeAccountField(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYEE_ACCOUNT_REVEAL);
}

export function canWriteCounterparty(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.COUNTERPARTY_WRITE);
}

export function canManageCounterparty(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.COUNTERPARTY_MANAGE);
}

export function canWritePayeeAccount(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYEE_ACCOUNT_WRITE);
}

export function canVerifyPayeeAccount(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYEE_ACCOUNT_VERIFY);
}

export function canManagePayeeAccount(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYEE_ACCOUNT_MANAGE);
}

export function canSeeFinanceTemplates(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.TEMPLATE_READ);
}

export function canSeeInvoices(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.INVOICE_READ);
}

export function canSeeInstallments(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.INSTALLMENT_READ);
}

export function canSeeAdjustments(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.ADJUSTMENT_READ);
}

export function canSeeFinanceSummary(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SUMMARY_READ);
}

/**
 * Returns true if the user can see the PaymentRecords page.
 */
export function canSeePaymentRecords(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PAYMENT_RECORD_READ);
}

/**
 * Returns true if the user can see the ProjectUnlocks page.
 */
export function canSeeProjectUnlocks(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.PROJECT_UNLOCK_READ);
}

/**
 * Returns true if the user can see the RevenueLedgers page.
 */
export function canSeeRevenueLedgers(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.REVENUE_LEDGER_READ);
}

/**
 * Returns true if the user can see the CommissionLedgers page.
 */
export function canSeeCommissionLedgers(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.COMMISSION_LEDGER_READ);
}

/**
 * Returns true if the user can see the Issue #372 multi-stage commission
 * workbench (rules + details + payout candidates + wide-template export).
 * Any of the four read-side permissions is enough to open the workbench;
 * action buttons inside the page are further gated by their own permission.
 */
export function canSeeCommission372(granted: readonly string[]): boolean {
  return (
    hasPermission(granted, FINANCE_PERMISSIONS.COMMISSION_RULE_READ) ||
    hasPermission(granted, FINANCE_PERMISSIONS.COMMISSION_DETAIL_READ) ||
    hasPermission(
      granted,
      FINANCE_PERMISSIONS.COMMISSION_PAYOUT_CANDIDATE_READ,
    ) ||
    hasPermission(granted, FINANCE_PERMISSIONS.COMMISSION_EXPORT_RENDER)
  );
}

// ─── Issue #373 — Sales Report helpers ────────────────────────────────────────

export function canSeeSalesReport(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_READ);
}

export function canGenerateSalesReport(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_GENERATE);
}

export function canReviewSalesReport(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_REVIEW);
}

export function canPublishSalesReport(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_PUBLISH);
}

export function canExportSalesReport(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_EXPORT);
}

export function canRevealSalesReportDetail(granted: readonly string[]): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.SALES_REPORT_REVEAL);
}

// ─── Issue #402 — Unified finance audit view helpers ─────────────────────────

export function canSeeFinanceAuditView(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_FULL,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_TEAM,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_SELF,
    FINANCE_PERMISSIONS.AUDIT_VIEW_READ_CHANNEL,
  ]);
}

export function canRevealFinanceAuditEntry(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.AUDIT_VIEW_REVEAL);
}

export function canExportFinanceAuditView(
  granted: readonly string[],
): boolean {
  return hasPermission(granted, FINANCE_PERMISSIONS.AUDIT_VIEW_EXPORT);
}
